import React from 'react';
import type { CarouselSlide } from './Carousel';

export type CarouselSettingsModel = {
  slides: CarouselSlide[];
  showDots?: boolean;
  showArrows?: boolean;
  variant?: 'classic' | 'cards' | 'minimal';
  dotPosition?: 'top' | 'bottom' | 'overlay-top' | 'overlay-bottom';
  dotStyle?: 'dot' | 'pill' | 'bar';
  transition?: 'subtle' | 'fade' | 'zoom' | 'slide' | 'none';
};

type Props = CarouselSettingsModel & {
  onChange: (next: CarouselSettingsModel) => void;
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-3">
    <span className="text-sm text-gray-800">{label}</span>
    {children}
  </label>
);

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean)=>void; ariaLabel: string }> = ({ checked, onChange, ariaLabel }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${checked ? 'bg-purple-600' : 'bg-gray-300'}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
  </button>
);

const CarouselSettings: React.FC<Props> = ({ slides, showDots = true, showArrows = true, variant='classic', dotPosition='top', dotStyle='dot', transition='subtle', onChange }) => {
  const Thumb: React.FC<{ src: string; onRemove: () => void }> = ({ src, onRemove }) => {
    const [ok, setOk] = React.useState(true);
    React.useEffect(()=>{ setOk(true); }, [src]);
    return (
      <div className="relative group rounded-lg ring-1 ring-gray-200 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        {ok ? (
          <img src={src} onError={()=> setOk(false)} className="h-14 w-full object-cover" />
        ) : (
          <div className="h-14 w-full flex items-center justify-center gap-2 text-gray-500">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><path d="M8.5 8.5h.01"/><path d="M21 15l-5-5L5 21"/></svg>
            <span className="text-[11px]">Image not found</span>
          </div>
        )}
        <button type="button" className="absolute top-1 right-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/90 border border-gray-200 text-gray-700 opacity-0 group-hover:opacity-100" onClick={onRemove}>
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
        </button>
      </div>
    );
  };
  const [sel, setSel] = React.useState(0);
  React.useEffect(()=>{ if (sel > (slides.length-1)) setSel(Math.max(0, slides.length-1)); }, [slides.length]);

  const update = (patch: Partial<Props>) => {
    onChange({
      slides: (patch.slides ?? slides) as any,
      showDots: (patch.showDots ?? showDots) as any,
      showArrows: (patch.showArrows ?? showArrows) as any,
      variant: (patch.variant ?? variant) as any,
      dotPosition: (patch.dotPosition ?? dotPosition) as any,
      dotStyle: (patch.dotStyle ?? dotStyle) as any,
      transition: (patch.transition ?? transition) as any,
    });
  };

  const slide = slides[sel] || {};
  const kind = (slide as any).kind || 'single';
  const [imgOk, setImgOk] = React.useState(true);
  React.useEffect(()=>{ setImgOk(true); }, [(slide as any).image]);

  const setSlide = (i: number, patch: any) => {
    const next = slides.map((s, idx) => idx === i ? { ...s, ...patch } : s);
    update({ slides: next as any });
  };
  const addSlide = () => {
    const next = [...slides, { kind: 'single', title: 'New card', text: 'Add a description…', image: '/uploads/sample.jpg' } as any];
    update({ slides: next as any });
    setSel(next.length - 1);
  };
  const removeSlide = () => {
    if (slides.length === 0) return;
    const next = slides.filter((_, i)=> i !== sel);
    update({ slides: next as any });
    setSel(Math.max(0, sel-1));
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="text-sm font-medium text-gray-900">Appearance</div>
        <Row label="Show navigation dots">
          <Toggle checked={!!showDots} onChange={(v)=> update({ showDots: v })} ariaLabel="Toggle dots" />
        </Row>
        <Row label="Show arrow buttons">
          <Toggle checked={!!showArrows} onChange={(v)=> update({ showArrows: v })} ariaLabel="Toggle arrows" />
        </Row>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-700">Template</span>
            <select value={variant} onChange={(e)=> update({ variant: e.target.value as any })} className="rounded-md border border-gray-200 bg-white px-2 py-1">
              <option value="classic">Classic</option>
              <option value="cards">Cards</option>
              <option value="minimal">Minimal</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-700">Dots position</span>
            <select value={dotPosition} onChange={(e)=> update({ dotPosition: e.target.value as any })} className="rounded-md border border-gray-200 bg-white px-2 py-1">
              <option value="top">Top</option>
              <option value="bottom">Bottom</option>
              <option value="overlay-top">Overlay top</option>
              <option value="overlay-bottom">Overlay bottom</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-700">Dots style</span>
            <select value={dotStyle} onChange={(e)=> update({ dotStyle: e.target.value as any })} className="rounded-md border border-gray-200 bg-white px-2 py-1">
              <option value="dot">Dot</option>
              <option value="pill">Pill</option>
              <option value="bar">Bar</option>
            </select>
          </label>
        </div>
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium text-gray-900">Content</div>
        <div className="flex items-center gap-2">
          <select className="rounded-md border border-gray-200 bg-white px-2 py-1 text-sm" value={String(sel)} onChange={(e)=> setSel(Number(e.target.value))}>
            {slides.map((s, i)=> (
              <option key={i} value={i}>{(s.title || `Slide ${i+1}`)}</option>
            ))}
          </select>
          <button type="button" className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs hover:bg-gray-50" onClick={addSlide}>
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            Add slide
          </button>
          {slides.length>0 && (
            <button type="button" className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50" onClick={removeSlide}>
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
              Remove
            </button>
          )}
        </div>

        {slides.length>0 && (
          <div className="space-y-2 rounded-lg border border-gray-200 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-gray-600">Card type</label>
              <select className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs" value={kind} onChange={(e)=> {
                const k = e.target.value as any;
                if (k === 'single') setSlide(sel, { kind: 'single', image: (slide as any).images?.[0] || (slide as any).image, images: undefined });
                else setSlide(sel, { kind: 'gallery', images: (slide as any).images || ((slide as any).image ? [(slide as any).image] : []) });
              }}>
                <option value="single">Single image</option>
                <option value="gallery">Gallery</option>
              </select>
              {kind === 'gallery' ? (
                <>
                <button type="button" className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs hover:bg-gray-50" onClick={() => {
                  const onPick = (ev: any) => {
                    try {
                      const arr: string[] = (ev?.detail?.urls) || [];
                      if (arr && arr.length) setSlide(sel, { kind: 'gallery', images: [ ...((slide as any).images||[]), ...arr ] });
                    } catch {}
                  };
                  try { window.addEventListener('media-picked', onPick, { once: true } as any); } catch {}
                  try { const ev: any = new CustomEvent('open-media-picker', { detail: { multiple: true } }); window.dispatchEvent(ev); } catch {}
                }}>
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                  Pick images
                </button>
                <label className="ml-2 flex items-center gap-2 text-xs text-gray-700">
                  <span>Transition</span>
                  <select value={(slide as any).transition ?? 'subtle'} onChange={(e)=> setSlide(sel, { transition: e.target.value as any })} className="rounded-md border border-gray-200 bg-white px-2 py-1">
                    <option value="subtle">Subtle</option>
                    <option value="fade">Fade</option>
                    <option value="zoom">Zoom</option>
                    <option value="slide">Slide</option>
                    <option value="none">None</option>
                  </select>
                </label>
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="h-14 w-28 overflow-hidden rounded-lg ring-1 ring-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                    {Boolean((slide as any).image) && imgOk ? (
                      <img src={(slide as any).image} onError={()=> setImgOk(false)} alt="Selected" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex items-center gap-2 px-2 text-gray-500">
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><path d="M8.5 8.5h.01"/><path d="M21 15l-5-5L5 21"/></svg>
                        <span className="text-xs whitespace-nowrap">{(slide as any).image ? 'Image not found' : 'No image'}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Pick image"
                      className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 hover:bg-purple-50 hover:border-purple-200"
                      onClick={() => {
                        const onPick = (ev: any) => {
                          try {
                            const arr: string[] = (ev?.detail?.urls) || [];
                            const first = arr[0];
                            if (first) setSlide(sel, { image: first });
                          } catch {}
                        };
                        try { window.addEventListener('media-picked', onPick, { once: true } as any); } catch {}
                        try { const ev: any = new CustomEvent('open-media-picker', { detail: { multiple: false } }); window.dispatchEvent(ev); } catch {}
                      }}
                    >
                      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                      Pick image
                    </button>
                    {Boolean((slide as any).image) && (
                      <button
                        type="button"
                        aria-label="Clear image"
                        className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2.5 py-1.5 text-xs text-red-700 hover:bg-red-50"
                        onClick={()=> setSlide(sel, { image: '' })}
                      >
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <input className="mt-2 w-full rounded-md border border-gray-200 px-2 py-1 text-sm font-semibold text-gray-900" value={(slide as any).title || ''} onChange={(e)=> setSlide(sel, { title: e.target.value })} placeholder="Card title" />
            <textarea className="w-full rounded-md border border-gray-200 px-2 py-1 text-sm text-gray-700" value={(slide as any).text || ''} onChange={(e)=> setSlide(sel, { text: e.target.value })} placeholder="Short description" />
            {kind === 'gallery' && (
              <div className="mt-2 grid grid-cols-6 gap-2">
                {((slide as any).images || []).map((src: string, i: number) => (
                  <Thumb key={i} src={src} onRemove={() => { const arr = [ ...((slide as any).images||[]) ]; arr.splice(i,1); setSlide(sel, { images: arr }); }} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CarouselSettings;
